import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/Layout/MainLayout";
import Loader from "../../../components/Common/Loader";
import { BackIcon } from "../../../components/Dashboard/DataCenterIcons";
import { commonService } from "../../../services/commonService";
import "../../../assets/styles/PccountsPage.css";
import { ROUTES } from "../../../utils/constants";

interface HeaderField {
  id: number;
  header_name: string;
}

interface RecordItem {
  id: number;
  row_data: Record<string, any>;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
}

// Edit/Delete on this page used to be open to any logged-in user regardless
// of role - restricted to pc (17), pc_manager (16), and admin (1) only.
// Everyone else still sees the icons, just disabled, rather than hidden.
const CAN_EDIT_DELETE_ROLE_IDS = ['1', '16', '17'];

const PccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const canEditDelete = CAN_EDIT_DELETE_ROLE_IDS.includes(localStorage.getItem('role_id') || '');

  const [headers, setHeaders] = useState<HeaderField[]>([]);
  const [records, setRecords] = useState<RecordItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchHeaders();
  }, []);

  useEffect(() => {
    if (headers.length > 0) {
      fetchRecords();
    }
  }, [page, limit, headers]);

  // ✅ Fetch headers (for ID → Name mapping)
  const fetchHeaders = async () => {
    try {
      const response = await commonService.cdget();

      let activeHeaders =
        (response?.data || [])
          .filter((item: any) => !item.is_deleted)
          .reverse()
          .map((item: any) => ({
            id: item.id,
            header_name: item.header_name
          }));

      // 👉 Find indexes
      const contactIndex = activeHeaders.findIndex(
        (h: any) => h.header_name === "Contact Counts"
      );

      const directDialIndex = activeHeaders.findIndex(
        (h: any) => h.header_name === "Direct Dials/Phone"
      );

      // 👉 Move "Direct Dials/Phone" after "Contact Counts"
      if (contactIndex !== -1 && directDialIndex !== -1) {
        const [directDialItem] = activeHeaders.splice(directDialIndex, 1);
        activeHeaders.splice(contactIndex + 1, 0, directDialItem);
      }

      setHeaders(activeHeaders);
    } catch (error) {
      console.error("Failed to fetch headers");
    }
  };

  // ✅ Fetch records
  const fetchRecords = async () => {
    try {
      setLoading(true);
      const response = await commonService.recordGet(page, limit, search);

      setRecords(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch records");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: number) => {
    if (!canEditDelete) return;
    navigate(`${ROUTES.EDIT_CD_RECORD}?id=${id}`)
  };

  const handleDeleteClick = (id: number) => {
    if (!canEditDelete) return;
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedId) return;

    try {
      await commonService.recordDelete(selectedId);
      fetchRecords();
    } catch (error) {
      console.error("Delete failed");
    } finally {
      setShowDeleteModal(false);
      setSelectedId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSelectedId(null);
  };

  const totalPages = Math.ceil(total / limit);

  const convertToIST = (utcString: any) => {
    const date = new Date(utcString);

    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });
  };


  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    fetchRecords();
  };

  const clearSearch = async () => {
    setSearch("");
    try {
      setLoading(true);
      const response = await commonService.recordGet(page, limit, "");

      setRecords(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch records");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="pc-counts-page custom-scrollbar">
        <main className="pc-counts-main">
          {/* Back */}
          <div className="pc-counts-header">
            <button onClick={() => navigate(ROUTES.CD_DASH)} className="pc-counts-back-button">
              <BackIcon />
              Back
            </button>
          </div>

          <div className="pc-counts-dashboard">
            <div className="pc-counts-dashboard-header">
              <h2 className="pc-counts-dashboard-title">Counts Dashboard</h2>
              <div className="pc-counts-actions">
                <button
                  className="pc-counts-back-button"
                  onClick={() => navigate(ROUTES.CREATE_CD_RECORDS)}
                >
                  Add New Count Record
                </button>
                <button
                  className="pc-counts-back-button"
                  onClick={() => navigate(ROUTES.BULK_UPLOAD_CD_RECORDS)}
                >
                  Upload Csv
                </button>
                <button
                  onClick={() => navigate(ROUTES.MANAGE_DATA_POINTS)}
                  className="pc-counts-back-button"
                >
                  Manage Data Points
                </button>
              </div>
            </div>

            {/* Controls */}
            <div className="pc-counts-table-controls">
              <div className="pc-counts-show-entries">
                <span>Show</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="500">500</option>
                </select>
                <span>entries</span>
              </div>
              <div className="pc-counts-search" style={{ position: "relative" }}>
                <input
                  type="text"
                  style={{ outline: "none", width: "336px", height: "34px" }}
                  placeholder="Search..."
                  value={search}
                  onKeyUp={handleKeyDown}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                />
                {search && (
                  <span
                    onClick={clearSearch}
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      fontWeight: "bold"
                    }}
                  >
                    ×
                  </span>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="pc-counts-table-wrapper" style={{ position: "relative" }}>

              {/* 🔥 Overlay Loader */}
              {loading && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(255, 255, 255, 0.7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10,
                  }}
                >
                  <Loader text="Loading records..." />
                </div>
              )}

              {/* Scrollable Table */}
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                <table
                  className="pc-counts-table"
                  style={{ marginBottom: "0", tableLayout: "fixed" }}
                >
                  <thead>
                    <tr>
                      {headers.map((header) => (
                        <th key={header.id}>{header.header_name}</th>
                      ))}
                      {!headers || headers.length !== 0 && (
                        <>
                          <th>Created By</th>
                          <th>Created At</th>
                          <th style={{ textAlign: "center" }}>Actions</th>
                        </>
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {!loading && records.length > 0 ? (
                      records.map((record) => (
                        <tr key={record.id}>
                          {headers.map((header) => (
                            <td key={header.id} data-label={header.header_name}>
                              {record.row_data[String(header.id)] || "-"}
                            </td>
                          ))}

                          <td data-label="Created By">
                            {(record.updated_by?.trim() || record.created_by?.trim())
                              ? (record.updated_by || record.created_by)
                                .toLowerCase()
                                .replace(/^./, (c) => c.toUpperCase())
                              : "-"}
                          </td>

                          <td data-label="Created At">
                            {record.updated_at
                              ? convertToIST(record.updated_at)
                              : record.created_at
                                ? convertToIST(record.created_at)
                                : "-"}
                          </td>

                          <td data-label="Actions" style={{ textAlign: "center" }}>
                            <div className="pc-actions">
                              <span
                                className={`pc-edit-icon${canEditDelete ? '' : ' disabled'}`}
                                onClick={() => handleEdit(record.id)}
                                title={canEditDelete ? "Edit" : "Only PC, PC Manager, and Admin can edit"}
                                style={{ fontSize: "10px", paddingTop: "4px" }}
                              >
                                ✏️
                              </span>

                              <span
                                className={`pc-delete-icon${canEditDelete ? '' : ' disabled'}`}
                                onClick={() => handleDeleteClick(record.id)}
                                title={canEditDelete ? "Delete" : "Only PC, PC Manager, and Admin can delete"}
                              >
                                🗑
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : !loading ? (
                      <tr>
                        <td colSpan={headers.length + 3} style={{ textAlign: "center" }}>
                          No records found.
                        </td>
                      </tr>
                    ) : <tr style={{height:"90px"}}></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="pc-counts-pagination-footer">
              <span>
                Showing {(page - 1) * limit + 1} to{" "}
                {Math.min(page * limit, total)} of {total} entries
              </span>

              <div className="pc-counts-pagination">
                <span
                  onClick={() => page > 1 && setPage(page - 1)}
                  style={{ cursor: page > 1 ? "pointer" : "not-allowed" }}
                >
                  {"<"}
                </span>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => {
                    return (
                      p === 1 || // first
                      p === totalPages || // last
                      (p >= page - 2 && p <= page + 2) // around current
                    );
                  })
                  .reduce<(number | string)[]>((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) {
                      acc.push("...");
                    }
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, index) => (
                    <a
                      key={index}
                      className={p === page ? "active" : ""}
                      onClick={() => p !== "..." && setPage(Number(p))}
                      style={{
                        cursor: p === "..." ? "default" : "pointer",
                        margin: "0 0px",
                      }}
                    >
                      {p}
                    </a>
                  ))}

                <span
                  onClick={() => page < totalPages && setPage(page + 1)}
                  style={{ cursor: page < totalPages ? "pointer" : "not-allowed" }}
                >
                  {">"}
                </span>
              </div>
            </div>
          </div>
        </main>
      </div>
      {showDeleteModal && (
        <div className="pc-modal-overlay">
          <div className="pc-modal">
            <h3>Delete Record</h3>
            <p>Are you sure you want to delete this record?</p>

            <div className="pc-modal-actions">
              <button
                className="pc-modal-cancel"
                onClick={cancelDelete}
              >
                Cancel
              </button>

              <button
                className="pc-modal-delete"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default PccountsPage;